'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Type helpers ─────────────────────────────────────────────────────────────

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|function.*does not exist|syntax error|type.*does not exist/i.test(error.message);
  }
  return false;
}

// ─── Listing type (matches DB snake_case) ────────────────────────────────────

export interface DBListing {
  id: string;
  owner_id: string;
  title: string;
  house_type: string;
  bhk: string;
  bedrooms: number;
  bathrooms: number;
  built_up_area: number;
  furnishing: string;
  parking: boolean;
  water_facility: boolean;
  power_backup: boolean;
  lift: boolean;
  wifi: boolean;
  floor_number: number;
  facing: string;
  available_from: string;
  available_from_raw: string;
  monthly_rent: number;
  security_deposit: number;
  maintenance_charges: number;
  negotiable: boolean;
  address: string;
  locality: string;
  city: string;
  state: string;
  pincode: string;
  landmarks: string;
  google_maps_link: string;
  photos: string[];
  cover_photo: string;
  status: 'Active' | 'Pending Review' | 'Rejected' | 'Draft' | 'Rented Out';
  views: number;
  inquiries_count: number;
  verified: boolean;
  featured: boolean;
  owner_name: string;
  owner_phone: string;
  owner_email: string;
  rejection_reason: string;
  reviewed_at: string;
  listed_on: string;
  created_at: string;
  listing_number?: string;
  validity_date?: string;
  expires_at?: string;
  payment_status?: string;
  payment_id?: string;
  razorpay_order_id?: string;
  contact_click_count?: number;
  owner_inquiry_response?: string;
}

// ─── Listings ─────────────────────────────────────────────────────────────────

export const listingService = {
  /** Fetch all active listings (public browse) — only paid listings */
  async getActiveListings(): Promise<DBListing[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('status', 'Active')
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: any) {
      console.error('listingService.getActiveListings:', e.message);
      throw e;
    }
  },

  /** Fetch listings owned by the current user */
  async getOwnerListings(ownerId: string): Promise<DBListing[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('owner_id', ownerId)
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: any) {
      console.error('listingService.getOwnerListings:', e.message);
      throw e;
    }
  },

  /** Fetch all listings for admin */
  async getAllListings(): Promise<DBListing[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: any) {
      console.error('listingService.getAllListings:', e.message);
      throw e;
    }
  },

  /** Create a new listing */
  async createListing(listing: Omit<DBListing, 'id' | 'created_at'>): Promise<DBListing | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('listings')
        .insert(listing)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (e: any) {
      console.error('listingService.createListing:', e.message);
      throw e;
    }
  },

  /** Update a listing */
  async updateListing(id: string, updates: Partial<DBListing>): Promise<DBListing | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('listings')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (e: any) {
      console.error('listingService.updateListing:', e.message);
      throw e;
    }
  },

  /** Delete a listing */
  async deleteListing(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('listings').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      console.error('listingService.deleteListing:', e.message);
      throw e;
    }
  },

  /** Admin: approve a listing */
  async approveListing(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('listings')
        .update({ status: 'Active', reviewed_at: new Date().toLocaleDateString('en-GB') })
        .eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      console.error('listingService.approveListing:', e.message);
      throw e;
    }
  },

  /** Admin: reject a listing */
  async rejectListing(id: string, reason: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('listings')
        .update({ status: 'Rejected', rejection_reason: reason, reviewed_at: new Date().toLocaleDateString('en-GB') })
        .eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      console.error('listingService.rejectListing:', e.message);
      throw e;
    }
  },

  /** Increment view count */
  async incrementViews(id: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase.rpc('increment_listing_views', { listing_id: id }).maybeSingle();
    } catch {
      // non-critical
    }
  },
};

// ─── Inquiries ────────────────────────────────────────────────────────────────

export interface DBInquiry {
  id: string;
  listing_id: string;
  tenant_id: string | null;
  tenant_name: string;
  tenant_email: string;
  tenant_phone: string;
  message: string;
  created_at: string;
}

export const inquiryService = {
  async createInquiry(inquiry: Omit<DBInquiry, 'id' | 'created_at'>): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('inquiries').insert(inquiry);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      // Increment inquiries_count on listing
      await supabase.rpc('increment_listing_inquiries', { listing_id: inquiry.listing_id }).maybeSingle();
      return true;
    } catch (e: any) {
      console.error('inquiryService.createInquiry:', e.message);
      return false;
    }
  },

  async getInquiriesForAdmin(): Promise<DBInquiry[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('inquiries')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: any) {
      console.error('inquiryService.getInquiriesForAdmin:', e.message);
      return [];
    }
  },
};

// ─── Reports ──────────────────────────────────────────────────────────────────

export interface DBReport {
  id: string;
  listing_id: string;
  reporter_id: string | null;
  reporter_name: string;
  reason: string;
  status: 'Open' | 'Resolved' | 'Dismissed';
  created_at: string;
}

export const reportService = {
  async createReport(report: Omit<DBReport, 'id' | 'created_at'>): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('reports').insert(report);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      console.error('reportService.createReport:', e.message);
      return false;
    }
  },

  async getAllReports(): Promise<DBReport[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('reports')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: any) {
      console.error('reportService.getAllReports:', e.message);
      return [];
    }
  },

  async updateReportStatus(id: string, status: DBReport['status']): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('reports').update({ status }).eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      console.error('reportService.updateReportStatus:', e.message);
      return false;
    }
  },
};

// ─── User management (admin) ──────────────────────────────────────────────────

export interface DBUserProfile {
  id: string;
  email: string;
  full_name: string;
  phone: string;
  role: 'owner' | 'tenant' | 'admin';
  is_blocked: boolean;
  created_at: string;
}

export const userService = {
  async getAllUsers(): Promise<DBUserProfile[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: any) {
      console.error('userService.getAllUsers:', e.message);
      return [];
    }
  },

  async blockUser(userId: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_blocked: true })
        .eq('id', userId);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      console.error('userService.blockUser:', e.message);
      return false;
    }
  },

  async unblockUser(userId: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_blocked: false })
        .eq('id', userId);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      console.error('userService.unblockUser:', e.message);
      return false;
    }
  },
};

// ─── Photo Upload ─────────────────────────────────────────────────────────────

export const photoService = {
  /**
   * Upload a single photo file to Supabase Storage and return the public URL.
   * Files are stored under: listing-photos/{userId}/{timestamp}-{filename}
   */
  async uploadPhoto(file: File, userId: string): Promise<string | null> {
    const supabase = createClient();
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { data, error } = await supabase.storage
        .from('listing-photos')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });
      if (error) {
        console.error('photoService.uploadPhoto:', error.message);
        return null;
      }
      const { data: urlData } = supabase.storage
        .from('listing-photos')
        .getPublicUrl(data.path);
      return urlData.publicUrl;
    } catch (e: any) {
      console.error('photoService.uploadPhoto:', e.message);
      return null;
    }
  },

  /**
   * Upload multiple photo files and return array of public URLs.
   * Skips failed uploads and continues with the rest.
   */
  async uploadPhotos(files: File[], userId: string): Promise<string[]> {
    const results = await Promise.all(files.map((f) => this.uploadPhoto(f, userId)));
    return results.filter((url): url is string => url !== null);
  },
};

// ─── Payment records ──────────────────────────────────────────────────────────

export interface DBPayment {
  id: string;
  listing_id: string | null;
  owner_id: string | null;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  amount: number;
  currency: string;
  status: string;
  listing_title: string;
  listing_type: string;
  owner_name: string;
  owner_email: string;
  owner_phone: string;
  failure_reason: string;
  created_at: string;
  updated_at: string;
}

export const paymentService = {
  async getAllPayments(): Promise<DBPayment[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: any) {
      console.error('paymentService.getAllPayments:', e.message);
      return [];
    }
  },

  async getPaymentsByOwner(ownerId: string): Promise<DBPayment[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('owner_id', ownerId)
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: any) {
      console.error('paymentService.getPaymentsByOwner:', e.message);
      return [];
    }
  },
};

// ─── Location notifications ───────────────────────────────────────────────────

export interface DBLocationNotification {
  id: string;
  user_id: string;
  city: string;
  state: string;
  email: string;
  is_active: boolean;
  created_at: string;
}

export const locationNotificationService = {
  async subscribe(data: { user_id: string; city: string; state: string; email: string }): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('location_notifications').upsert(
        { ...data, is_active: true },
        { onConflict: 'user_id,city' }
      );
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      console.error('locationNotificationService.subscribe:', e.message);
      return false;
    }
  },

  async getSubscribersByCity(city: string): Promise<DBLocationNotification[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('location_notifications')
        .select('*')
        .ilike('city', city)
        .eq('is_active', true);
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: any) {
      console.error('locationNotificationService.getSubscribersByCity:', e.message);
      return [];
    }
  },

  async getAllSubscriptions(): Promise<DBLocationNotification[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('location_notifications')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: any) {
      console.error('locationNotificationService.getAllSubscriptions:', e.message);
      return [];
    }
  },
};

// ─── Hero Carousel Slides ─────────────────────────────────────────────────────

export interface DBHeroSlide {
  id: string;
  sort_order: number;
  image: string;
  mobile_image: string;
  alt: string;
  title: string;
  description: string;
  cta_text: string;
  cta_link: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const heroSlideService = {
  async getActiveSlides(): Promise<DBHeroSlide[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('hero_carousel_slides')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: any) {
      console.error('heroSlideService.getActiveSlides:', e.message);
      return [];
    }
  },

  async getAllSlides(): Promise<DBHeroSlide[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('hero_carousel_slides')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: any) {
      console.error('heroSlideService.getAllSlides:', e.message);
      return [];
    }
  },

  async createSlide(slide: Omit<DBHeroSlide, 'id' | 'created_at' | 'updated_at'>): Promise<DBHeroSlide | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('hero_carousel_slides')
        .insert(slide)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (e: any) {
      console.error('heroSlideService.createSlide:', e.message);
      return null;
    }
  },

  async updateSlide(id: string, updates: Partial<DBHeroSlide>): Promise<DBHeroSlide | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('hero_carousel_slides')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (e: any) {
      console.error('heroSlideService.updateSlide:', e.message);
      return null;
    }
  },

  async deleteSlide(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('hero_carousel_slides')
        .delete()
        .eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      console.error('heroSlideService.deleteSlide:', e.message);
      return false;
    }
  },
};
